import { EditorSelection, StateEffect, StateField } from "@codemirror/state";
import { Decoration, EditorView, WidgetType } from "@codemirror/view";
import { undo } from "@codemirror/commands";
import { ancestorNodeOfType } from "components/organisms/manuscript/editor/latex/tree/ancestors";

export const pastedContentEffect = StateEffect.define();

export const insertPastedContent = (view, { latex, text }) =>
    view.state.changeByRange((range) => {
        if (ancestorNodeOfType(view.state, range.anchor, "$MathContainer")) {
            return {
                range: EditorSelection.cursor(range.from + text.length),
                changes: {
                    from: range.from,
                    to: range.to,
                    insert: text,
                },
            };
        }

        return {
            range: EditorSelection.cursor(range.from + latex.length),
            changes: {
                from: range.from,
                to: range.to,
                insert: latex || text,
            },
        };
    });

export const storePastedContent = (content, formatted) => ({
    effects: pastedContentEffect.of({ content, formatted }),
});

const pastedContentTheme = EditorView.baseTheme({
    ".ol-cm-pasted-content-menu-toggle": {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        background: "none",
        borderRadius: "8px",
        border: "1px solid rgb(125, 125, 125)",
        color: "inherit",
        margin: "0 4px",
        opacity: "0.7",
        "&:hover": {
            opacity: "1",
        },
    },
    ".ol-cm-pasted-content-menu-popover": {
        position: "absolute",
        zIndex: "40",
        minWidth: "220px",
        padding: "6px 0",
        border: "1px solid #d7e1eb",
        borderRadius: "10px",
        background: "#ffffff",
        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.18)",
    },
    ".ol-cm-pasted-content-menu": {
        display: "flex",
        flexDirection: "column",
        fontSize: "13px",
    },
    ".ol-cm-pasted-content-menu-item": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        width: "100%",
        padding: "8px 14px",
        border: 0,
        background: "transparent",
        color: "#30465c",
        textAlign: "left",
        cursor: "pointer",
        "&:hover": {
            background: "#f4f8fb",
        },
    },
    ".ol-cm-pasted-content-menu-item-label": {
        flex: "1 1 auto",
    },
    ".ol-cm-pasted-content-menu-item-shortcut": {
        color: "#708398",
        fontSize: "11px",
        whiteSpace: "nowrap",
    },
});

class PastedContentMenuWidget extends WidgetType {
    constructor(pastedContent, formatted) {
        super();
        this.pastedContent = pastedContent;
        this.formatted = formatted;
    }

    toDOM(view) {
        const wrapper = document.createElement("span");
        wrapper.style.position = "relative";

        const button = document.createElement("button");
        button.type = "button";
        button.className = "ol-cm-pasted-content-menu-toggle";
        button.setAttribute("aria-haspopup", "menu");
        button.setAttribute("aria-expanded", "false");

        const copyLabel = document.createElement("span");
        copyLabel.textContent = "Paste";
        const modeLabel = document.createElement("span");
        modeLabel.textContent = this.formatted ? "LaTeX" : "Text";
        const chevron = document.createElement("span");
        chevron.textContent = "▾";
        button.append(copyLabel, modeLabel, chevron);

        const popover = document.createElement("div");
        popover.className = "ol-cm-pasted-content-menu-popover";
        popover.hidden = true;

        const menu = document.createElement("div");
        menu.className = "ol-cm-pasted-content-menu";
        menu.setAttribute("role", "menu");

        const createItem = (label, shortcut, formatted) => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "ol-cm-pasted-content-menu-item";
            item.setAttribute("role", "menuitem");

            const left = document.createElement("span");
            left.className = "ol-cm-pasted-content-menu-item-label";
            left.textContent = label;

            const right = document.createElement("span");
            right.className = "ol-cm-pasted-content-menu-item-shortcut";
            right.textContent = shortcut;

            item.append(left, right);
            item.addEventListener("click", () => {
                this.insertPastedContent(view, this.pastedContent, formatted);
                popover.hidden = true;
                button.setAttribute("aria-expanded", "false");
                view.focus();
            });
            return item;
        };

        menu.append(
            createItem("Paste with formatting", "Mod+V", true),
            createItem("Paste without formatting", "Mod+Shift+V", false),
        );
        popover.appendChild(menu);

        const closeMenu = () => {
            popover.hidden = true;
            button.setAttribute("aria-expanded", "false");
        };

        const toggleMenu = () => {
            const nextOpen = popover.hidden;
            popover.hidden = !nextOpen;
            button.setAttribute("aria-expanded", nextOpen ? "true" : "false");
        };

        button.addEventListener("mousedown", (event) => event.preventDefault());
        button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleMenu();
        });

        const handleOutsideClick = (event) => {
            if (!wrapper.contains(event.target)) {
                closeMenu();
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        wrapper.addEventListener("DOMNodeRemoved", () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        });

        wrapper.append(button, popover);
        return wrapper;
    }

    insertPastedContent(view, pastedContent, formatted) {
        undo(view);
        view.dispatch(
            insertPastedContent(view, {
                latex: formatted ? pastedContent.latex : pastedContent.text,
                text: pastedContent.text,
            }),
        );
        view.dispatch(storePastedContent(pastedContent, formatted));
    }

    eq(widget) {
        return widget.pastedContent === this.pastedContent && widget.formatted === this.formatted;
    }
}

export const pastedContent = StateField.define({
    create() {
        return null;
    },
    update(value, transaction) {
        if (transaction.docChanged) {
            value = null;
        } else {
            for (const effect of transaction.effects) {
                if (effect.is(pastedContentEffect)) {
                    value = {
                        ...effect.value,
                        selection: transaction.state.selection,
                    };
                }
            }
        }
        return value;
    },
    provide(field) {
        return [
            EditorView.decorations.compute([field], (state) => {
                const value = state.field(field);
                if (!value) {
                    return Decoration.none;
                }

                return Decoration.set(
                    [
                        Decoration.widget({
                            widget: new PastedContentMenuWidget(value.content, value.formatted),
                            side: 1,
                        }).range(value.selection.main.to),
                    ],
                    true,
                );
            }),
            pastedContentTheme,
        ];
    },
});
